// src/pages/UtilityAllowanceJoinPage.js
// "Don't see your housing authority?" onboarding form, linked from the
// utility allowance hub. Collects everything needed to build an authority's
// HUSM schedule and submits it to the compliance service, which saves it and
// emails the team (no third-party form relay). Then on to the payment page.
// Route: /utility-allowances/join

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Brand from '../components/Brand';
import { paymentDestination, PRICES } from '../config/uaPayment';

// Same compliance-service base the UA schedule pages use. Join submissions
// are saved to its DB (ua_join_requests + the uploaded files) and the service
// sends the notification email itself, so every request is captured even if
// the email fails or checkout is abandoned.
const COMPLIANCE_API =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const T = {
  blue600: '#1d4ed8', // blue-700: reads at 4.5:1 on the blue-100 chips (WCAG 1.4.3),
  blue50: '#eff6ff',
  blue100: '#dbeafe',
  gray900: '#111827',
  gray800: '#1f2937',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const inputStyle = {
  width: '100%',
  padding: '0.7rem 0.9rem',
  fontSize: '0.92rem',
  border: `1px solid ${T.gray200}`,
  borderRadius: T.radiusSm,
  outline: 'none',
  fontFamily: T.font,
  color: T.gray900,
  background: T.white,
  boxSizing: 'border-box',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: T.gray800,
  marginBottom: '0.35rem',
};

function Field({ label, required, children }) {
  // Give a single form control an id and point the label at it (WCAG 1.3.1, 3.3.2);
  // groups such as YesNo keep their own per-option labels.
  const autoId = React.useId();
  // A group (YesNo, chips) ignores the id it is given, so its own option labels still apply.
  // Comments and whitespace around the control are not elements, so look through them.
  const kids = React.Children.toArray(children);
  const isControl = (k) => React.isValidElement(k) && (typeof k.type === 'string'
    ? ['input', 'select', 'textarea'].includes(k.type)
    : ('value' in k.props || 'onChange' in k.props || 'type' in k.props || 'rows' in k.props));
  const controls = kids.filter(isControl);
  const single = controls.length === 1 ? controls[0] : null;
  const id = single ? (single.props.id || autoId) : undefined;
  const control = single && !single.props.id ? kids.map(k => (k === single ? React.cloneElement(k, { id }) : k)) : children;
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label} {required && <span style={{ color: T.blue600 }}>*</span>}
      </label>
      {control}
    </div>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      style={inputStyle}
      onFocus={e => { e.target.style.borderColor = T.blue600; e.target.style.boxShadow = `0 0 0 3px ${T.blue50}`; }}
      onBlur={e => { e.target.style.borderColor = T.gray200; e.target.style.boxShadow = 'none'; }}
    />
  );
}

function YesNo({ name, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '1.25rem', paddingTop: '0.15rem' }}>
      {['Yes', 'No'].map(v => (
        <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: T.gray800, cursor: 'pointer' }}>
          <input
            type="radio"
            name={name}
            value={v}
            required
            onChange={onChange ? () => onChange(v) : undefined}
            style={{ accentColor: T.blue600, width: 16, height: 16 }}
          />
          {v}
        </label>
      ))}
    </div>
  );
}

export default function UtilityAllowanceJoinPage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Shown under the button when the save fails or times out. The visitor is
  // not sent to payment in that case: there would be no request to pay for.
  const [submitError, setSubmitError] = useState('');
  // Drives both the price note under the submit button and the post-submit
  // redirect: members pay $300/yr, non-members $350/yr.
  const [isMember, setIsMember] = useState(null);
  // Every schedule file picked so far. The native <input type="file"> only
  // ever shows its latest selection, so an authority with several schedules
  // (one per program or building type) could not tell whether earlier picks
  // survived. We accumulate picks here, list them, and send them all.
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  // Per-file and total upload caps (base64 fits well within the service's
  // 50mb JSON body limit).
  const MAX_FILE_BYTES = 12 * 1024 * 1024;
  const MAX_TOTAL_BYTES = 40 * 1024 * 1024;
  const MAX_FILES = 20;

  const fmtSize = (n) => n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

  const addFiles = (picked) => {
    const next = [...files];
    const errors = [];
    for (const f of Array.from(picked || [])) {
      if (next.some(x => x.name === f.name && x.size === f.size && x.lastModified === f.lastModified)) continue;
      if (f.size > MAX_FILE_BYTES) { errors.push(`${f.name} is over 12 MB.`); continue; }
      if (next.length >= MAX_FILES) { errors.push(`Up to ${MAX_FILES} files.`); break; }
      if (next.reduce((a, x) => a + x.size, 0) + f.size > MAX_TOTAL_BYTES) { errors.push(`${f.name} would push the total over 40 MB.`); continue; }
      next.push(f);
    }
    setFiles(next);
    setFileError(errors.join(' '));
    // Clear the native input so picking the same file again re-fires onChange,
    // and so its own (single-selection) display never contradicts our list.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setFileError('');
  };

  // Save the submission (every uploaded schedule, base64) to the compliance
  // service, which writes the DB row, emails the team, and answers {ok, id}.
  // On success the visitor goes to the payment page at their rate; on a
  // failed or timed-out save they stay here with an inline error and can try
  // again. The save is awaited with a timeout so a slow service holds things
  // up a few seconds at most.
  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (isSubmitting) return;

    if (files.length === 0) {
      setFileError('Please upload your current utility allowance schedule.');
      if (fileInputRef.current) fileInputRef.current.focus();
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    const fd = new FormData(form);
    (async () => {
      const filePayloads = [];
      for (const file of files) {
        try {
          const base64 = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(String(r.result).split(',')[1]);
            r.onerror = reject;
            r.readAsDataURL(file);
          });
          filePayloads.push({ name: file.name, type: file.type, data: base64 });
        } catch {}
      }
      const save = fetch(`${COMPLIANCE_API}/api/utility-allowance/join-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          housingAuthority: fd.get('Housing Authority'),
          cityState: fd.get('City and State'),
          phaCode: fd.get('PHA Code'),
          contactName: fd.get('Contact Name'),
          title: fd.get('Title'),
          email: fd.get('email'),
          phone: fd.get('Phone'),
          households: fd.get('Households Across All Programs'),
          s8aMember: fd.get('Section 8 Administrators Member'),
          hasChatbotTranslation: fd.get('Has Chatbot and Translation Library'),
          nextScheduleMonth: fd.get('Next Schedule Month'),
          notes: fd.get('Notes'),
          website: fd.get('_honey'),
          files: filePayloads,
          file: filePayloads[0] || null,
        }),
      }).then(r => r.json()).catch(() => null);
      const saved = await Promise.race([save, new Promise(resolve => setTimeout(() => resolve(null), 8000 + 4000 * filePayloads.length))]);
      if (!saved || !saved.ok) {
        setSubmitError('We could not save your request. Please email contact@gethousingai.com or try again.');
        setIsSubmitting(false);
        return;
      }
      // Remember who they are so the payment page can prefill the
      // alternative-payment form and link it back to this request.
      try {
        localStorage.setItem('ua_join_info', JSON.stringify({
          id: saved.id || null,
          housingAuthority: fd.get('Housing Authority'),
          email: fd.get('email'),
        }));
      } catch {}
      // Straight to checkout at their member rate ($300 member / $350
      // standard). See src/config/uaPayment.js.
      window.location.assign(paymentDestination(isMember === 'Yes', window.location.origin, saved.id, fd.get('email')));
    })();
  };

  return (
    <div style={{
      fontFamily: T.font,
      minHeight: '100vh',
      background: T.gradient,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header — sticky frosted, centered branding, back pill pinned left,
          same structure as the schedule viewer pages */}
      <header style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.gray200}`,
        padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          justifyContent: mobile ? 'flex-start' : 'center',
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
        }}>
          <div style={mobile ? { flexShrink: 0 } : { position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
            <button
              onClick={() => navigate('/utility-allowances')}
              style={{
                background: T.gray100,
                border: `1px solid ${T.gray200}`,
                borderRadius: T.radiusPill,
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: T.gray600,
                padding: '0.45rem 0.85rem',
                transition: 'all 0.15s ease',
                fontFamily: T.font,
                fontWeight: 600,
              }}
              title="Back to schedules"
              onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.borderColor = T.blue600; e.currentTarget.style.color = T.blue600; }}
              onMouseOut={e => { e.currentTarget.style.background = T.gray100; e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.color = T.gray600; }}
            >
              ←
            </button>
          </div>
          <Brand as="a" href="https://gethousingai.com" target="_blank" rel="noopener noreferrer" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', fontFamily: T.font, textDecoration: 'none' }} />
          <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
          <span style={{
            fontSize: mobile ? '0.72rem' : '0.85rem',
            padding: mobile ? '0.2rem 0.55rem' : '0.25rem 0.75rem',
            background: T.blue100,
            color: T.blue600,
            borderRadius: T.radiusPill,
            fontWeight: 600,
          }}>
            Get Your Schedule · Utility Allowances
          </span>
        </div>
      </header>

      {/* Main */}
      <main style={{ padding: mobile ? '1.25rem 0.75rem' : '2.5rem 1.5rem', flex: 1 }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <div style={{
            textAlign: 'center',
            marginBottom: mobile ? '1.25rem' : '2rem',
          }}>
            <h1 style={{
              fontSize: mobile ? '1.35rem' : '1.9rem',
              fontWeight: 800,
              color: T.gray900,
              marginBottom: '0.6rem',
              letterSpacing: '-0.02em',
            }}>
              Get Your Utility Allowance Schedule
            </h1>
            <p style={{ color: T.gray600, fontSize: mobile ? '0.84rem' : '0.92rem', lineHeight: 1.6, maxWidth: '520px', margin: '0 auto' }}>
              Tell us about your housing authority and we&rsquo;ll build your HUSM-computed,
              fully cited utility allowance schedule, with every rate sourced to its filing,
              ready for annual review under 24 CFR &sect; 982.517. This is a yearly
              subscription: we build your first schedule for whenever you need it,
              and each January&nbsp;1st after that we regenerate it with current rates,
              together with every participating authority, on one shared annual cycle.
            </p>
          </div>

          {/* What you get */}
          <div style={{
            background: T.white,
            border: `1px solid ${T.gray200}`,
            borderRadius: T.radius,
            boxShadow: T.shadowSm,
            padding: mobile ? '1.1rem 1rem' : '1.5rem 1.75rem',
            marginBottom: mobile ? '1.25rem' : '1.75rem',
          }}>
            <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: T.gray900, margin: '0 0 0.85rem' }}>
              What your subscription includes
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
              gap: mobile ? '0.7rem' : '0.85rem 1.5rem',
            }}>
              {[
                { title: 'Zero staff hours', body: 'The annual review happens without anyone on your team touching a spreadsheet or a tariff sheet.' },
                { title: 'Nothing to learn, nothing to install', body: 'No software, no training, no data entry. Your schedule arrives finished.' },
                { title: 'The 10% test, in writing', body: 'At each annual subscription renewal we run the 10% check: a category-by-category comparison against your prior year’s schedule, with a written basis for revising or not revising. A defensible record either way.' },
                { title: 'Line-by-line math shown', body: 'Not just the final allowance amount. We include the actual calculation behind every figure, so you know the basis for every number on the chart.' },
                { title: 'Every unit size and configuration', body: 'Full bedroom-size matrix across heating types and structure types, so the chart works for every voucher in your program.' },
                { title: 'Instant portability lookups', body: 'Your purchase of an annual UA chart includes access to every other participating PHA’s chart, on demand, always current.' },
                { title: 'One synced annual cycle', body: 'Your initial schedule within a week, then regenerated with every authority each January 1st.' },
                { title: 'Works anywhere', body: 'Rate sourcing for any jurisdiction in the US.' },
              ].map(b => (
                <div key={b.title} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ color: T.blue600, fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.5 }}>✓</span>
                  <div>
                    <div style={{ fontWeight: 700, color: T.gray900, fontSize: '0.82rem' }}>{b.title}</div>
                    <div style={{ color: T.gray600, fontSize: '0.78rem', lineHeight: 1.5 }}>{b.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
              onSubmit={handleSubmit}
              style={{
                background: T.white,
                border: `1px solid ${T.gray200}`,
                borderRadius: T.radius,
                boxShadow: T.shadow,
                padding: mobile ? '1.25rem 1rem' : '2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.1rem',
              }}
            >
              {/* Honeypot: hidden from people, filled by bots. The service
                  silently accepts and discards any submission that fills it. */}
              <input type="text" name="_honey" tabIndex={-1} autoComplete="off" style={{ display: 'none' }} />

              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: T.gray900, margin: 0, borderBottom: `1px solid ${T.gray100}`, paddingBottom: '0.5rem' }}>
                Housing Authority
              </h3>
              <Field label="Housing authority name" required>
                <TextInput type="text" name="Housing Authority" placeholder="e.g. Salem Housing Authority" required />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: '1.1rem' }}>
                <Field label="City / Town & State" required>
                  <TextInput type="text" name="City and State" placeholder="e.g. Salem, MA" required />
                </Field>
                <Field label="PHA code (if known)">
                  <TextInput type="text" name="PHA Code" placeholder="e.g. MA055" />
                </Field>
              </div>
              <Field label="Households served across all programs (approximate)" required>
                <TextInput type="text" name="Households Across All Programs" placeholder="e.g. 450 (HCV vouchers + public housing units)" required />
              </Field>

              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: T.gray900, margin: '0.4rem 0 0', borderBottom: `1px solid ${T.gray100}`, paddingBottom: '0.5rem' }}>
                Contact
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: '1.1rem' }}>
                <Field label="Your name" required>
                  <TextInput type="text" name="Contact Name" placeholder="Full name" required />
                </Field>
                <Field label="Title / role">
                  <TextInput type="text" name="Title" placeholder="e.g. Section 8 Director" />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: '1.1rem' }}>
                <Field label="Work email" required>
                  <TextInput type="email" name="email" placeholder="you@yourha.org" required />
                </Field>
                <Field label="Phone">
                  <TextInput type="tel" name="Phone" placeholder="(555) 555-5555" />
                </Field>
              </div>

              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: T.gray900, margin: '0.4rem 0 0', borderBottom: `1px solid ${T.gray100}`, paddingBottom: '0.5rem' }}>
                About Your Authority
              </h3>
              <Field label="Are you a Section 8 Administrators member?" required>
                <YesNo name="Section 8 Administrators Member" onChange={setIsMember} />
              </Field>
              <Field label="Do you have the HousingAI chatbot and translation library?" required>
                <YesNo name="Has Chatbot and Translation Library" />
              </Field>
              <Field label="What month do you need your next utility allowance schedule for?" required>
                <select
                  name="Next Schedule Month"
                  required
                  defaultValue=""
                  style={{ ...inputStyle, appearance: 'auto', cursor: 'pointer' }}
                  onFocus={e => { e.target.style.borderColor = T.blue600; e.target.style.boxShadow = `0 0 0 3px ${T.blue50}`; }}
                  onBlur={e => { e.target.style.borderColor = T.gray200; e.target.style.boxShadow = 'none'; }}
                >
                  <option value="" disabled>Select a month</option>
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July',
                    'August', 'September', 'October', 'November', 'December'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Field>
              <Field label="Please upload your current utility allowance schedule(s)" required>
                {/* No `name` on purpose: the files are read from the `files` list in
                    handleSubmit and sent base64 to the service, not via FormData.
                    Not `required` either: the native input is cleared after each
                    pick (the list below is the source of truth), so handleSubmit
                    enforces "at least one file" itself. */}
                <input
                  id="ua-current-schedule"
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.txt"
                  onChange={e => addFiles(e.target.files)}
                  style={{ ...inputStyle, padding: '0.55rem 0.9rem', cursor: 'pointer' }}
                />
                {files.length > 0 && (
                  <ul style={{ listStyle: 'none', margin: '0.5rem 0 0', padding: 0, display: 'grid', gap: '0.35rem' }}>
                    {files.map((f, i) => (
                      <li
                        key={`${f.name}-${f.size}-${f.lastModified}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.6rem',
                          padding: '0.45rem 0.7rem', borderRadius: 8,
                          background: T.gray100, border: `1px solid ${T.gray200}`,
                          fontSize: '0.8rem', color: T.gray800,
                        }}
                      >
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.name}>
                          {f.name}
                        </span>
                        <span style={{ color: T.gray500, flexShrink: 0 }}>{fmtSize(f.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          aria-label={`Remove ${f.name}`}
                          style={{
                            flexShrink: 0, border: 'none', background: 'transparent',
                            color: T.gray500, cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0 0.2rem',
                          }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {fileError && (
                  <p role="alert" style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: '#b91c1c' }}>{fileError}</p>
                )}
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: T.gray500 }}>
                  {files.length > 0
                    ? `${files.length} file${files.length === 1 ? '' : 's'} attached. Choose more to add another — each one you pick stays in the list.`
                    : 'PDF, Word, Excel, or an image of the schedule — whatever you have. Several schedules? Add them one at a time or select them together; each stays listed here. Max 12 MB per file.'}
                </p>
              </Field>
              <Field label="Anything else we should know?">
                <textarea
                  name="Notes"
                  rows={3}
                  placeholder="Program size, current schedule vendor, timing, questions…"
                  style={{ ...inputStyle, resize: 'vertical' }}
                  onFocus={e => { e.target.style.borderColor = T.blue600; e.target.style.boxShadow = `0 0 0 3px ${T.blue50}`; }}
                  onBlur={e => { e.target.style.borderColor = T.gray200; e.target.style.boxShadow = 'none'; }}
                />
              </Field>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  marginTop: '0.25rem',
                  padding: '0.85rem',
                  background: T.blue600,
                  color: T.white,
                  border: 'none',
                  borderRadius: T.radiusPill,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                  fontFamily: T.font,
                  transition: 'background 0.2s ease',
                }}
                onMouseOver={e => { if (!isSubmitting) e.currentTarget.style.background = '#1d4ed8'; }}
                onMouseOut={e => { e.currentTarget.style.background = T.blue600; }}
              >
                {isSubmitting ? 'Sending…' : 'Purchase My Schedule'}
              </button>
              {submitError && (
                <p role="alert" style={{ margin: 0, textAlign: 'center', fontSize: '0.82rem', color: '#b91c1c', lineHeight: 1.5 }}>
                  {submitError}
                </p>
              )}
              <p style={{ margin: 0, textAlign: 'center', fontSize: '0.8rem', color: T.gray500, lineHeight: 1.5 }}>
                Annual subscription: <b style={{ color: T.gray800 }}>
                  {isMember === 'Yes' ? `$${PRICES.member}/year (Section 8 Administrators member rate)`
                    : isMember === 'No' ? `$${PRICES.standard}/year`
                    : `$${PRICES.standard}/year, or $${PRICES.member}/year for Section 8 Administrators members`}
                </b>.
                Includes your initial schedule within a week, plus the January&nbsp;1st annual regeneration.
                You&rsquo;ll be taken to secure checkout after submitting.
              </p>
            </form>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ background: T.gray900, color: T.white, padding: mobile ? '1.25rem 0' : '2rem 0', fontFamily: T.font }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto', padding: '0 1rem',
          display: 'flex', flexDirection: mobile ? 'column' : 'row',
          flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
          gap: mobile ? '0.5rem' : '1rem',
        }}>
          <Brand theme="dark" markClassName="text-blue-600"
            style={{ fontWeight: 600, fontSize: '0.95rem' }} />
          <div style={{ fontSize: mobile ? '0.78rem' : '0.875rem', color: '#d1d5db' }}>
            © 2026 HousingAI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
